import { Box, Grid, Typography } from "@mui/material";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { useContext, useMemo, useState } from "react";
import { mapClusterUsinasToEquipmentDowntime } from "../../utils/availabilityDowntimeMapper";
import {
    AvailabilityAnalysisClusterAverage,
    AvailabilityAnalysisData,
    AvailabilityAnalysisPowerPlantAverage,
    ClusterUnavailabilityData,
    RopCard1111AvailabilityAnalysisTaticoAggregated,
    RopCard1114EquipmentsUnavailabilityTaticoAggregated,
} from "../../types";

const EMPTY_AVAILABILITY_FOR_CHART: AvailabilityAnalysisData = {
    systemAvailability: 0,
    systemExpectedValue: 0,
    inverterAvailability: 0,
    inverterExpectedValue: 0,
    contractualAvailability: 0,
    contractualExpectedValue: 0,
    averageInverterUnavailabilityTime: 0,
    averageTrackerUnavailabilityTime: 0,
    averageStringUnavailabilityTime: 0,
};
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
//import AvailabilityAnalysisClusterCard from "./AvailabilityAnalysisClusterCard";
import AvailabilityAnalysisTacticalChart, {
    ChartDataItem,
} from "./AvailabilityAnalysisTacticalChart";
import DowntimeByEquipmentSection from "./DowntimeByEquipmentSection";
import AvailabilityAnalyInfoCard, { AvailabilityAnalyInfoCardProps } from "./AvailabilityAnalyInfoCard";
import { GoPulse } from "react-icons/go";
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import InsertChartOutlinedOutlinedIcon from '@mui/icons-material/InsertChartOutlinedOutlined';
import AnaliseConsolidadaDisponibilidade from "./AnaliseConsolidadaDisponibilidade";
import AvailabilityAnalysisCards from "./AvailabilityAnalysisCards";
import CloseIcon from '@mui/icons-material/Close';

export type AnaliseDisponibilidadeTacticalProps = {
    data: RopCard1111AvailabilityAnalysisTaticoAggregated;
    equipmentsUnavailability: RopCard1114EquipmentsUnavailabilityTaticoAggregated;
};

export default function AnaliseDisponibilidadeTactical({ data, equipmentsUnavailability }: AnaliseDisponibilidadeTacticalProps) {
    const { theme } = useContext(MainContext);
    const equipmentsCards: AvailabilityAnalyInfoCardProps[] = [
        {
            title: "Sistema",
            icon: <GoPulse fontSize={14} color="#60A5FA" />,
            value: `${(data.overallAverage.systemAvailability * 100).toFixed(2)}%`,
            valueColor: "#60A5FA"
        },
        {
            title: "Inversores",
            icon: <Inventory2OutlinedIcon style={{ color: "#34D399", fontSize: 14 }} />,
            value: `${(data.overallAverage.inverterAvailability * 100).toFixed(2)}%`,
            valueColor: "#34D399"
        },
        {
            title: "Contratual",
            icon: <FeedOutlinedIcon sx={{ fontSize: 14, color: "#A78BFA" }} />,
            value: `${(data.overallAverage.contractualAvailability * 100).toFixed(2)}%`,
            valueColor: "#A78BFA"
        },
        {
            title: "Eventos Críticos",
            icon: <WarningAmberIcon sx={{ fontSize: 14, color: "#EF4444" }} />,
            value: `${equipmentsUnavailability.clusters.reduce((acc, count) => acc + count.criticalEventCount, 0)}`,
            valueColor: "#EF4444"
        },
        {
            title: "Total Usinas",
            icon: <SettingsOutlinedIcon style={{ color: "#60A5FA", fontSize: 14 }} />,
            value: `${data.clusterAverages.reduce((acc, cluster) => acc + (cluster.usinasCount || 0), 0)}`,
            valueColor: "#60A5FA"
        },
        {
            title: "Total Clusters",
            icon: <InsertChartOutlinedOutlinedIcon style={{ color: "#F59E0B", fontSize: 14 }} />,
            value: `${data.clusterAverages.filter(cluster => cluster.clusterId).length}`,
            valueColor: "#F59E0B"
        }
    ]

    const [selectedCluster, setSelectedCluster] = useState<AvailabilityAnalysisClusterAverage | null>(null);
    const [selectedClusterUnavailabilityData, setSelectedClusterUnavailabilityData] = useState<ClusterUnavailabilityData | null>(null);

    const findClusterByKey = (clusterKey: string | null) => {
        if (!clusterKey) return null;
        return (
            data.clusterAverages.find(
                (cluster) =>
                    cluster.clusterId === clusterKey ||
                    cluster.clusterName === clusterKey ||
                    (cluster.clusterName || "Sem Cluster") === clusterKey,
            ) ?? null
        );
    };

    const handleClusterClick = (clusterKey: string | null) => {
        const cluster = findClusterByKey(clusterKey);
        const isSameCluster =
            selectedCluster &&
            cluster &&
            selectedCluster.clusterId === cluster.clusterId &&
            selectedCluster.clusterName === cluster.clusterName;

        if (isSameCluster) {
            setSelectedCluster(null);
            setSelectedClusterUnavailabilityData(null);
            return;
        }

        setSelectedCluster(cluster);
        setSelectedClusterUnavailabilityData(
            equipmentsUnavailability.clusters.find(
                (c) =>
                    (cluster?.clusterId && c.clusterId === cluster.clusterId) ||
                    c.clusterName === cluster?.clusterName,
            ) ?? null,
        );
    };

    const allPowerPlantAverages: AvailabilityAnalysisPowerPlantAverage[] = useMemo(() => {
        const byId = new Map<number, AvailabilityAnalysisPowerPlantAverage>();
        for (const cluster of data.clusterAverages) {
            for (const p of cluster.powerPlantAverages) {
                if (!byId.has(p.usinaId)) {
                    byId.set(p.usinaId, p);
                }
            }
        }
        return Array.from(byId.values()).sort((a, b) =>
            a.usinaName.localeCompare(b.usinaName, "pt-BR", { sensitivity: "base" }),
        );
    }, [data.clusterAverages]);

    const selectedUsinaRows = useMemo(() => {
        if (!selectedCluster) return [];

        const byId = new Map<number, string>();
        for (const p of selectedCluster.powerPlantAverages) {
            byId.set(p.usinaId, p.usinaName);
        }
        for (const u of selectedClusterUnavailabilityData?.usinas ?? []) {
            byId.set(u.usinaId, u.usinaName);
        }
        return Array.from(byId.entries())
            .map(([usinaId, usinaName]) => ({ usinaId, usinaName }))
            .sort((a, b) =>
                a.usinaName.localeCompare(b.usinaName, "pt-BR", { sensitivity: "base" }),
            );
    }, [selectedCluster, selectedClusterUnavailabilityData]);

    const selectedClusterUsinasUnavailability = useMemo(
        () => selectedClusterUnavailabilityData?.usinas ?? [],
        [selectedClusterUnavailabilityData],
    );

    const powerPlantByUsinaId = useMemo(() => {
        const m = new Map<number, AvailabilityAnalysisPowerPlantAverage>();
        for (const p of allPowerPlantAverages) {
            m.set(p.usinaId, p);
        }
        return m;
    }, [allPowerPlantAverages]);

    const equipmentDowntimeGroups = useMemo(
        () =>
            mapClusterUsinasToEquipmentDowntime(
                selectedUsinaRows,
                selectedClusterUsinasUnavailability,
            ),
        [selectedUsinaRows, selectedClusterUsinasUnavailability],
    );

    const clusterUsinasChartData: ChartDataItem[] | null = selectedCluster
        ? selectedUsinaRows.map((row) => {
            const p = powerPlantByUsinaId.get(row.usinaId);
            const d = p?.data ?? EMPTY_AVAILABILITY_FOR_CHART;
            const isDisponibilidadeOnOffEnabled = (d as any).disponibilidadeOnOffEnabled ?? true;
            return {
                name: row.usinaName,
                clusterId: p?.clusterId ?? null,
                items: [
                    ...(isDisponibilidadeOnOffEnabled ? [{
                        itemName: "Sistema",
                        itemValue: d.systemAvailability * 100,
                        itemMeta: d.systemExpectedValue * 100,
                        color: "#60A5FA",
                    }] : []),
                    {
                        itemName: "Inversores",
                        itemValue: d.inverterAvailability * 100,
                        itemMeta: d.inverterExpectedValue * 100,
                        color: "#34D399",
                    },
                    ...(isDisponibilidadeOnOffEnabled ? [{
                        itemName: "Contratual",
                        itemValue: d.contractualAvailability * 100,
                        itemMeta: d.contractualExpectedValue * 100,
                        color: "#A78BFA",
                    }] : []),
                ],
            };
        })
        : null;

    return (
        <Grid
            item
            xs={12}
            marginY={"12px"}
            sx={{
                height: "100%",
                padding: "16px",
                paddingBottom: 0,
                marginLeft: "12px",
                backgroundColor: theme.cardBoxBackgroudColor,
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column"
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>
                        Análise de Disponibilidade
                    </Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: "400", color: theme?.secondaryTextColor || "#6B7280" }}>
                        Sistema, Inversores e Contratual em uma única visão
                    </Typography>
                </Box>
            </Box>
            <Grid container>
                <Grid item xs={12} sx={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "12px" }}>
                    <FilterAltOutlinedIcon sx={{ color: theme.primaryTextColor, fontSize: 20 }} />
                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: theme.primaryTextColor,
                        }}
                    >
                        Visão Consolidada por Cluster
                    </Typography>
                </Grid>
                <Grid item xs={12} >
                    <AvailabilityAnalysisCards data={data} onClusterClick={handleClusterClick} />

                    {selectedCluster &&
                        <>
                            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginTop: "16px", marginBottom: "8px", paddingX: "4px" }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", paddingLeft: "8px" }}>
                                    <Box>
                                        <Typography sx={{ fontSize: "16px", textAlign: "left", fontWeight: "600", color: theme.primaryTextColor }}>
                                            Cluster - {selectedCluster.clusterName}
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: "16px", marginTop: "4px" }}>
                                            <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor }}>
                                                {selectedCluster.usinasCount ?? 0} usinas
                                            </Typography>

                                            <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexDirection: "row" }}>
                                                <InsertChartOutlinedOutlinedIcon sx={{ fontSize: 14, color: theme.secondaryTextColor }} />
                                                <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor }}>
                                                    {selectedClusterUnavailabilityData?.eventCount ?? 0}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: "12px", color: "#EF4444", display: "flex", alignItems: "center", gap: "4px" }}>
                                                <WarningAmberIcon sx={{ fontSize: 14 }} />
                                                {selectedClusterUnavailabilityData?.criticalEventCount ?? 0}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box>
                                    <CloseIcon
                                        sx={{
                                            cursor: "pointer",
                                            color: theme.primaryTextColor,
                                            fontSize: 20,
                                        }}
                                        onClick={() => {
                                            setSelectedCluster(null);
                                            setSelectedClusterUnavailabilityData(null);
                                        }}
                                    />
                                </Box>
                            </Box>
                            {clusterUsinasChartData && clusterUsinasChartData.length > 0 && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        width: "100%",
                                        flexShrink: 0,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            color: theme.primaryTextColor,
                                            marginBottom: 1,
                                            marginTop: 1,
                                            textAlign: "left",
                                        }}
                                    >
                                        Disponibilidade por Usina
                                    </Typography>
                                    <AvailabilityAnalysisTacticalChart
                                        data={clusterUsinasChartData}
                                        showLegend={true}
                                        fitContent
                                    />
                                    <DowntimeByEquipmentSection
                                        equipmentGroups={equipmentDowntimeGroups}
                                    />
                                </Box>
                            )}
                        </>
                    }
                    {
                    /*                     {viewMode === 'cluster'
                        ? data.clusterAverages.map((cluster, index) => (
                            <AvailabilityAnalysisClusterCard
                                key={index}
                                cluster={cluster}
                                index={index}
                            />
                        ))
                        : [].map((powerPlant, index) => (
                            <AvailabilityAnalysisPowerPlantCard
                                key={index}
                                powerPlant={powerPlant}
                                index={index}
                            />
                        ))
                    } */}
                </Grid>

                <Grid container xs={12} spacing={0.5}>
                    {equipmentsCards.map((card, idx) => (
                        <AvailabilityAnalyInfoCard key={idx} {...card} backgroundColor="#202020b0" gridXS={2} />
                    ))}
                </Grid>
                <Grid item xs={12} sx={{ marginTop: "8px" }}>
                    <AnaliseConsolidadaDisponibilidade
                        sistemaPercentage={data.overallAverage.systemAvailability}
                        sistemaMeta={data.overallAverage.systemExpectedValue}
                        inversoresPercentage={data.overallAverage.inverterAvailability}
                        inversoresMeta={data.overallAverage.inverterExpectedValue}
                        contratualPercentage={data.overallAverage.contractualAvailability}
                        contratualMeta={data.overallAverage.contractualExpectedValue}
                        eventosCriticos={4}
                        sistemaTarget={90}
                        inversoresTarget={98}
                        contratualTarget={95}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}